import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminBoardProvider } from "@/contexts/AdminBoardContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieConsent from "@/components/CookieConsent";

// Debug banner to verify env injection - REMOVE AFTER DEBUGGING
const DebugBanner = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const isProduction = process.env.NODE_ENV === 'production';
  const isMissing = !apiUrl || apiUrl === 'undefined' || apiUrl === '';
  
  // Only show in development OR if there's a problem in production
  if (isProduction && !isMissing) return null;
  
  return (
    <div 
      data-testid="debug-banner"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '8px 16px',
        fontSize: '12px',
        fontFamily: 'monospace',
        backgroundColor: isMissing ? '#dc2626' : '#16a34a',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <span>
        <strong>ENV DEBUG:</strong> REACT_APP_API_URL = {apiUrl ? `"${apiUrl}"` : '<MISSING!>'}
      </span>
      <span>
        NODE_ENV: {process.env.NODE_ENV} | 
        Status: {isMissing ? '❌ API URL NOT SET' : '✅ OK'}
      </span>
    </div>
  );
};

// Public Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Contact from "@/pages/Contact";
import Resources from "@/pages/Resources";
import ApplicationStatus from "@/pages/ApplicationStatus";
import PlanSelection from "@/pages/PlanSelection";
import OnboardingQualification from "@/pages/OnboardingQualification";
import EmailVerification from "@/pages/EmailVerification";
import OnboardingCheckout from "@/pages/OnboardingCheckout";
import OnboardingSuccess from "@/pages/OnboardingSuccess";
import SetupWizard from "@/pages/SetupWizard";

// Protected Pages
import Dashboard from "@/pages/Dashboard";
import VoiceCall from "@/pages/VoiceCall";
import Agents from "@/pages/Agents";
import CallHistory from "@/pages/CallHistory";
import PhoneSettings from "@/pages/PhoneSettings";
import PhoneProvisioning from "@/pages/PhoneProvisioning";
import Contacts from "@/pages/Contacts";
import Campaigns from "@/pages/Campaigns";
import Leads from "@/pages/Leads";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import AgentTemplates from "@/pages/AgentTemplates";
import CancelSubscription from "@/pages/CancelSubscription";
import PrivacySettings from "@/pages/PrivacySettings";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import Channels from "@/pages/Channels";
import Phonebook from "@/pages/Phonebook";
import CampaignQueues from "@/pages/CampaignQueues";
import PerformanceDashboard from "@/pages/PerformanceDashboard";
import CallScheduling from "@/pages/CallScheduling";
import WhatsAppOnboarding from "@/pages/WhatsAppOnboarding";
import AgentConfiguration from "@/pages/AgentConfiguration";
import OnboardingWizard from "@/pages/OnboardingWizard";
import Layout from "@/components/Layout";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplications from "@/pages/admin/Applications";
import CommandCenter from "@/pages/admin/CommandCenter";
import AdminClients from "@/pages/admin/AdminClients";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminUsageDashboard from "@/pages/admin/AdminUsageDashboard";
import AdminBillingControls from "@/pages/admin/AdminBillingControls";
import SystemMonitor from "@/pages/admin/SystemMonitor";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminRevenue from "@/pages/admin/AdminRevenue";
// Enterprise Admin Pages
import AdminSecurityMFA from "@/pages/admin/AdminSecurityMFA";
import AdminSecuritySessions from "@/pages/admin/AdminSecuritySessions";
import AdminSubAdmins from "@/pages/admin/AdminSubAdmins";
import AdminEnterpriseAgents from "@/pages/admin/AdminEnterpriseAgents";
import AdminCallIntelligence from "@/pages/admin/AdminCallIntelligence";
import AdminNotificationSettings from "@/pages/admin/AdminNotificationSettings";
import AdminExternalAgents from "@/pages/admin/AdminExternalAgents";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminEmailMarketing from "@/pages/admin/AdminEmailMarketing";
import AdminAICopilot from "@/pages/admin/AdminAICopilot";
import AdminLayout from "@/components/AdminLayout";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<PlanSelection />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/application-status" element={<ApplicationStatus />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Onboarding Flow (Public) */}
            <Route path="/onboarding/qualify" element={<OnboardingQualification />} />
            <Route path="/onboarding/verify-email" element={<EmailVerification />} />
            <Route path="/onboarding/checkout" element={<OnboardingCheckout />} />
            <Route path="/onboarding/success" element={<OnboardingSuccess />} />
            <Route path="/setup-wizard" element={<SetupWizard />} />
            
            {/* Onboarding Wizard (Protected but no layout) */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } />
            
            {/* Protected Business Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="call" element={<VoiceCall />} />
              <Route path="call/:agentId" element={<VoiceCall />} />
              <Route path="agents" element={<Agents />} />
              <Route path="agent-templates" element={<AgentTemplates />} />
              <Route path="agent-config" element={<AgentConfiguration />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="leads" element={<Leads />} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/cancel" element={<CancelSubscription />} />
              <Route path="privacy" element={<PrivacySettings />} />
              <Route path="settings" element={<Settings />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="channels" element={<Channels />} />
              <Route path="phonebook" element={<Phonebook />} />
              <Route path="campaigns-queue" element={<CampaignQueues />} />
              <Route path="performance" element={<PerformanceDashboard />} />
              <Route path="scheduling" element={<CallScheduling />} />
              <Route path="whatsapp-onboarding" element={<WhatsAppOnboarding />} />
              <Route path="history" element={<CallHistory />} />
              <Route path="phone" element={<PhoneSettings />} />
              <Route path="phone/provision" element={<PhoneProvisioning />} />
            </Route>
            
            {/* Billing Routes (outside layout for clean checkout flow) */}
            <Route path="/billing/success" element={
              <ProtectedRoute>
                <BillingSuccess />
              </ProtectedRoute>
            } />
            <Route path="/billing/cancel" element={
              <ProtectedRoute>
                <BillingCancel />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes - wrapped with AdminBoardProvider */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/*" element={
              <AdminBoardProvider>
                <Routes>
                  <Route path="dashboard" element={<AdminLayout />}>
                    <Route index element={<CommandCenter />} />
                    <Route path="legacy" element={<AdminDashboard />} />
                  </Route>
                  <Route path="clients" element={<AdminLayout />}>
                    <Route index element={<AdminClients />} />
                  </Route>
                  <Route path="customers" element={<AdminLayout />}>
                    <Route index element={<AdminCustomers />} />
                  </Route>
                  <Route path="approvals" element={<AdminLayout />}>
                    <Route index element={<AdminApprovals />} />
                  </Route>
                  <Route path="applications" element={<AdminLayout />}>
                    <Route index element={<AdminApplications />} />
                  </Route>
                  <Route path="audit" element={<AdminLayout />}>
                    <Route index element={<AdminAuditLogs />} />
                  </Route>
                  <Route path="settings" element={<AdminLayout />}>
                    <Route index element={<AdminSettings />} />
                  </Route>
                  <Route path="plans" element={<AdminLayout />}>
                    <Route index element={<AdminPlans />} />
                  </Route>
                  <Route path="usage" element={<AdminLayout />}>
                    <Route index element={<AdminUsageDashboard />} />
                  </Route>
                  <Route path="billing-controls" element={<AdminLayout />}>
                    <Route index element={<AdminBillingControls />} />
                  </Route>
                  <Route path="system-monitor" element={<AdminLayout />}>
                    <Route index element={<SystemMonitor />} />
                  </Route>
                  <Route path="revenue" element={<AdminLayout />}>
                    <Route index element={<AdminRevenue />} />
                  </Route>
                  <Route path="compliance" element={<AdminLayout />}>
                    <Route index element={<AdminCompliance />} />
                  </Route>
                  <Route path="marketing" element={<AdminLayout />}>
                    <Route index element={<AdminEmailMarketing />} />
                  </Route>
                  <Route path="copilot" element={<AdminLayout />}>
                    <Route index element={<AdminAICopilot />} />
                  </Route>
                  {/* Enterprise Admin Routes */}
                  <Route path="security/mfa" element={<AdminLayout />}>
                    <Route index element={<AdminSecurityMFA />} />
                  </Route>
                  <Route path="security/sessions" element={<AdminLayout />}>
                    <Route index element={<AdminSecuritySessions />} />
                  </Route>
                  <Route path="sub-admins" element={<AdminLayout />}>
                    <Route index element={<AdminSubAdmins />} />
                  </Route>
                  <Route path="agents" element={<AdminLayout />}>
                    <Route index element={<AdminEnterpriseAgents />} />
                  </Route>
                  <Route path="external-agents" element={<AdminLayout />}>
                    <Route index element={<AdminExternalAgents />} />
                  </Route>
                  <Route path="call-intelligence" element={<AdminLayout />}>
                    <Route index element={<AdminCallIntelligence />} />
                  </Route>
                  <Route path="notifications" element={<AdminLayout />}>
                    <Route index element={<AdminNotificationSettings />} />
                  </Route>
                </Routes>
              </AdminBoardProvider>
            } />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <CookieConsent />
        <DebugBanner />
      </AuthProvider>
    </div>
  );
}

export default App;
